import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Star, MapPin, Globe, Award, Users, ArrowRight, Check, AlertCircle, Instagram, Youtube, ArrowLeft, Eye } from "lucide-react";
import { Pill, SectionLabel, Chip, FullScreenLoader } from "@/components/jk/kit";
import { MonthCalendar, isoLocal, todayISO } from "@/components/jk/MonthCalendar";
import { formatINR, formatTime } from "@/lib/format";
import api from "@/lib/api";

const VALID_SPECIALISATIONS = new Set(["KP Astrology", "Nadi Astrology", "Tamil Astrology", "Vedic Astrology", "Numerology", "Vastu", "Palmistry", "Tarot", "Gemmology"]);

const MODE_LABELS = { phone: "Phone", "Phone Call": "Phone", video: "Video", "Video Call": "Video", in_person: "In-person", "In Person": "In-person" };

const Stars = ({ n, size = 14 }) => (
  <span className="inline-flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} className={i <= n ? "fill-[#111111] text-[#111111]" : "text-[#d8d4ce]"} strokeWidth={1.5} />
    ))}
  </span>
);

const PublicBooking = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "1";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [serviceId, setServiceId] = useState("");
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState("");
  const [groups, setGroups] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [products, setProducts] = useState([]);

  const [tier, setTier] = useState(1);
  const [mode, setMode] = useState("phone");
  const windowDays = data?.booking_window_days || 30;
  const maxDate = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + windowDays); return isoLocal(d); })();

  useEffect(() => {
    Promise.allSettled([
      api.get(`/public/astrologer/${slug}`),
      api.get("/public/products"),
    ]).then(([astroResult, productsResult]) => {
      if (astroResult.status === "fulfilled") {
        setData(astroResult.value.data);
        if (astroResult.value.data.services?.length) setServiceId(astroResult.value.data.services[0].id);
      } else {
        setNotFound(true);
      }
      if (productsResult.status === "fulfilled") {
        setProducts(productsResult.value.data.products || []);
      }
    }).finally(() => setLoading(false));
  }, [slug]);

  // Detect tier from IP geo on first data load
  useEffect(() => {
    if (!data) return;
    api.get("/public/detect-tier").then(({ data: td }) => {
      setTier(td.tier);
    }).catch(() => {});
  }, [data]);

  useEffect(() => {
    if (!data || !serviceId) return;
    const svc = data.services.find((s) => s.id === serviceId);
    if (!svc) return;
    const tierKey = tier === 2 ? "tier2_consultation_modes" : tier === 3 ? "tier3_consultation_modes" : "tier1_consultation_modes";
    const modes = (svc[tierKey] || svc.consultation_modes || []).filter(Boolean);
    setMode(modes.length ? modes[0] : "phone");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, tier, data]);

  const loadSlots = useCallback(() => {
    setSlotsLoading(true);
    setTime("");
    api.get(`/public/astrologer/${slug}/slots`, { params: { date, tier, service_id: serviceId } })
      .then(({ data }) => { setGroups(data.groups); setBlocked(!!data.blocked); })
      .catch(() => { setGroups(null); setBlocked(false); }).finally(() => setSlotsLoading(false));
  }, [slug, date, tier, serviceId]);

  useEffect(() => { if (data) loadSlots(); }, [data, loadSlots]);

  if (loading) return <FullScreenLoader />;
  if (notFound) return (
    <div className="flex h-[100dvh] flex-col items-center justify-center bg-[var(--jk-paper)] px-8 text-center">
      <p className="text-[18px] font-bold text-[#111111]">Page not found</p>
      <p className="mt-2 text-[14px] text-[#666666]">This booking link doesn&apos;t exist or has moved.</p>
    </div>
  );

  const a = data.astrologer;
  const active = data.active !== false;
  const service = data.services.find((s) => s.id === serviceId);

  const tierPrice = (s) => {
    if (tier === 2) return s.tier2_price_inr ?? s.price_inr;
    if (tier === 3) return s.tier3_price_inr ?? s.price_inr;
    return s.tier1_price_inr ?? s.price_inr;
  };

  const displayPrice = (s) => formatINR(tierPrice(s));

  const tierDuration = (s) => {
    if (tier === 2) return s.tier2_duration_minutes ?? s.duration_minutes;
    if (tier === 3) return s.tier3_duration_minutes ?? s.duration_minutes;
    return s.tier1_duration_minutes ?? s.duration_minutes;
  };

  const serviceModes = (() => {
    if (!service) return ["phone"];
    const tierKey = tier === 2 ? "tier2_consultation_modes" : tier === 3 ? "tier3_consultation_modes" : "tier1_consultation_modes";
    const modes = (service[tierKey] || service.consultation_modes || []).filter(Boolean);
    return modes.length ? modes : ["phone"];
  })();

  const canBook = serviceId && date && time && active;

  const proceed = () => {
    if (isPreview) return;
    const displayedPrice = service ? displayPrice(service) : "";
    const payload = { serviceId, service, date, time, tier, preselectedMode: mode, displayedPrice };
    sessionStorage.setItem(`jk_book_${slug}`, JSON.stringify(payload));
    navigate(`/${slug}/intake`, { state: payload });
  };

  const SlotGroup = ({ label, items }) => (!items || items.length === 0) ? null : (
    <div className="mt-4">
      <p className="mb-2 text-[12px] font-semibold text-[#666666]">{label}</p>
      <div className="grid grid-cols-3 gap-2">
        {items.map((s) => (
          <button
            key={s.time}
            data-testid={`slot-${s.time}`}
            disabled={!s.available}
            onClick={() => setTime(s.time)}
            className={`h-10 rounded-[10px] text-[13px] font-medium transition-all active:scale-[0.97] ${
              !s.available ? "cursor-not-allowed bg-[#f5f3f0] text-[#cfccc6] line-through"
                : time === s.time ? "bg-[#111111] text-white" : "border border-[#e5e5e5] bg-white text-[#111111]"
            }`}
          >
            {formatTime(s.time)}
          </button>
        ))}
      </div>
    </div>
  );

  const hasSlots = groups && (groups.morning?.length || groups.afternoon?.length || groups.evening?.length);

  return (
    <div className="relative mx-auto flex h-[100dvh] w-full max-w-[440px] flex-col overflow-hidden bg-[var(--jk-paper)]" data-testid="public-booking-screen">
      <div className="min-h-0 flex-1 overflow-y-auto pb-28">
        {/* Hero header */}
        <div className="relative w-full">
          {a.photo_url ? (
            <img
              src={a.photo_url}
              alt={a.full_name}
              data-testid="hero-image"
              className="h-[360px] w-full object-cover md:h-[400px]"
            />
          ) : (
            <div className="flex h-[360px] w-full items-center justify-center bg-gradient-to-br from-[#FFE4C4] to-[#F4A460] md:h-[400px]" data-testid="hero-image">
              <span className="text-7xl font-bold text-black/20">{(a.full_name || "?").trim().charAt(0).toUpperCase()}</span>
            </div>
          )}

          {/* Top scrim for legibility */}
          <div className="pointer-events-none absolute left-0 right-0 top-0 z-10 h-32 bg-gradient-to-b from-black/40 to-transparent" />

          {/* Preview back button */}
          {isPreview && (
            <button
              onClick={() => navigate(-1)}
              data-testid="preview-back"
              className="absolute left-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 text-[#111111] shadow-[0_2px_8px_rgba(0,0,0,0.12)] backdrop-blur-md transition-all active:scale-95"
            >
              <ArrowLeft size={20} strokeWidth={2} />
            </button>
          )}

          {/* Floating social buttons */}
          {(a.instagram_url || a.youtube_url) && (
            <div className="absolute right-4 top-4 z-20 flex gap-3">
              {a.instagram_url && (
                <a
                  href={a.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="social-instagram"
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 text-[#111111] shadow-[0_2px_8px_rgba(0,0,0,0.12)] backdrop-blur-md transition-all active:scale-95"
                >
                  <Instagram size={20} />
                </a>
              )}
              {a.youtube_url && (
                <a
                  href={a.youtube_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="social-youtube"
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 text-[#111111] shadow-[0_2px_8px_rgba(0,0,0,0.12)] backdrop-blur-md transition-all active:scale-95"
                >
                  <Youtube size={20} />
                </a>
              )}
            </div>
          )}
        </div>

        {/* Overlapping content sheet */}
        <div className="relative z-30 -mt-8 flex w-full flex-col rounded-t-3xl bg-white px-6 pb-2 pt-6">
          <h1 className="mb-1 text-[26px] font-bold leading-tight text-[#111111]" data-testid="public-astro-name">{a.full_name}</h1>
          {a.city && <p className="mb-4 flex items-center gap-1.5 text-sm text-gray-600" data-testid="location-text"><MapPin size={14} /> {a.city}</p>}
          <div className="flex flex-wrap gap-2">
            {(a.specialisations || []).filter((s) => VALID_SPECIALISATIONS.has(s)).map((s) => <Pill key={s} tone="light" className="border border-[#E9E7E1] !bg-[#FAF9F7]">{s}</Pill>)}
          </div>
        </div>

        {/* Trust bar */}
        <div className="grid grid-cols-3 divide-x divide-[#eceae5] border-b border-[#eceae5] bg-white py-4 text-center" data-testid="trust-bar">
          <div className="flex flex-col items-center"><Users size={16} className="text-[#666]" /><p className="mt-1 text-[15px] font-bold text-[#111111]">{data.trust.consultations}+</p><p className="text-[10px] text-[#666666]">Consults</p></div>
          <div className="flex flex-col items-center"><Award size={16} className="text-[#666]" /><p className="mt-1 text-[15px] font-bold text-[#111111]">{data.trust.years}+</p><p className="text-[10px] text-[#666666]">Yrs exp</p></div>
          <div className="flex flex-col items-center"><Globe size={16} className="text-[#666]" /><p className="mt-1 text-[15px] font-bold text-[#111111]">{(data.trust.languages || []).length}</p><p className="text-[10px] text-[#666666]">Languages</p></div>
        </div>

        <div className="px-6">
          {!active && (
            <div className="mt-5 flex items-start gap-3 rounded-[14px] border border-[#f0d4c2] bg-[var(--jk-peach)] p-4" data-testid="inactive-alert">
              <AlertCircle size={20} strokeWidth={1.9} className="mt-0.5 shrink-0 text-[#b4502a]" />
              <div>
                <p className="text-[14px] font-semibold text-[#111111]">Not accepting bookings right now</p>
                <p className="mt-0.5 text-[13px] leading-relaxed text-[#6b5b4e]">{a.full_name} is currently unavailable. Please check back later.</p>
              </div>
            </div>
          )}

          {a.about && (
            <div className="border-b border-[#eceae5] py-5">
              <SectionLabel className="mb-2">About</SectionLabel>
              <p className="text-[14px] leading-relaxed text-[#444]">{a.about}</p>
            </div>
          )}

          {active && (
          <>
          {/* Mode toggle */}
          {(
            <div className="pt-8">
              <SectionLabel className="mb-3">HOW WOULD YOU LIKE TO CONSULT?</SectionLabel>
              <div className="flex rounded-full bg-[#F0EDEA] p-1">
                {serviceModes.map((key) => (
                  <button
                    key={key}
                    data-testid={`mode-toggle-${key}`}
                    onClick={() => setMode(key)}
                    className={`flex-1 rounded-full py-2 text-[14px] font-semibold transition-colors ${mode === key ? "bg-[#111111] text-white" : "bg-transparent text-[#888888]"}`}
                  >
                    {MODE_LABELS[key] || key}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Services */}
          <div className="py-5">
            <SectionLabel className="mb-3">Select a service</SectionLabel>
            <div className="space-y-2.5" data-testid="service-options">
              {data.services.map((s) => (
                <button key={s.id} data-testid={`service-option-${s.id}`} onClick={() => setServiceId(s.id)}
                  className={`flex w-full items-center gap-3 rounded-[14px] border p-4 text-left transition-colors ${serviceId === s.id ? "border-[#111111] bg-white" : "border-[#e9e6e1] bg-white"}`}>
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${serviceId === s.id ? "border-[#111111] bg-[#111111]" : "border-[#cfccc6]"}`}>
                    {serviceId === s.id && <Check size={12} strokeWidth={3} className="text-white" />}
                  </span>
                  <div className="flex-1">
                    <p className="text-[15px] font-semibold text-[#111111]">{s.name}</p>
                    <p className="text-[12px] text-[#666666]">{tierDuration(s)} min</p>
                  </div>
                  <p className="text-[15px] font-bold text-[#111111]">
                    {displayPrice(s)}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Recommended Products */}
          {products.length > 0 && (
            <div className="border-t border-[#eceae5] py-5">
              <SectionLabel className="mb-3">Recommended Products</SectionLabel>
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-6 px-6" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                {products.map((p) => (
                  <div key={p.id} data-testid={`product-card-${p.id}`}
                    className="flex w-[156px] shrink-0 flex-col overflow-hidden rounded-[14px] border border-[#e9e6e1] bg-white">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.name} className="h-[120px] w-full object-cover" />
                    ) : (
                      <div className="h-[120px] w-full bg-[#f2f0ec]" />
                    )}
                    <div className="flex flex-1 flex-col p-3">
                      <p className="line-clamp-2 text-[12px] font-semibold leading-snug text-[#111]">{p.name}</p>
                      <p className="mt-1 text-[14px] font-bold text-[#111]">{formatINR(p.selling_price_inr)}</p>
                      <button
                        data-testid={`buy-now-${p.id}`}
                        onClick={() => navigate(`/products/${p.id}/buy?astrologer=${slug}`)}
                        className="mt-2 w-full rounded-[10px] bg-[#111111] py-2 text-[12px] font-semibold text-white transition-colors active:bg-[#333333]"
                      >
                        Buy Now
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Date picker */}
          <div className="border-t border-[#eceae5] py-5">
            <SectionLabel className="mb-3">Pick a date</SectionLabel>
            <MonthCalendar value={date} onSelect={setDate} minDate={todayISO()} maxDate={maxDate} testid="booking-calendar" />

            {/* Slots */}
            <div className="mt-4">
              {slotsLoading ? (
                <p className="py-6 text-center text-[13px] text-[#aaaaaa]">Loading slots…</p>
              ) : blocked ? (
                <p className="py-6 text-center text-[13px] text-[#aaaaaa]" data-testid="no-slots">Not available on this day. Try another date.</p>
              ) : hasSlots ? (
                <div data-testid="slots">
                  <SlotGroup label="Morning" items={groups.morning} />
                  <SlotGroup label="Afternoon" items={groups.afternoon} />
                  <SlotGroup label="Evening" items={groups.evening} />
                </div>
              ) : (
                <p className="py-6 text-center text-[13px] text-[#aaaaaa]" data-testid="no-slots">No slots available on this day. Try another date.</p>
              )}
            </div>
          </div>
          </>
          )}

          {/* Reviews */}
          {data.reviews?.length > 0 && (
            <div className="border-t border-[#eceae5] py-5">
              <SectionLabel className="mb-3">Reviews</SectionLabel>
              <div className="space-y-3" data-testid="reviews">
                {data.reviews.slice(0, 5).map((r) => (
                  <div key={r.id} className="rounded-[14px] border border-[#e9e6e1] bg-white p-4">
                    <div className="flex items-center justify-between"><span className="text-[14px] font-semibold text-[#111111]">{r.reviewer_display}</span><Stars n={r.rating} /></div>
                    {r.text && <p className="mt-2 text-[13px] leading-relaxed text-[#555]">{r.text}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Sticky Book Now */}
      <div className="absolute bottom-0 left-0 right-0 mx-auto max-w-[440px] border-t border-[#e9e6e1] bg-[var(--jk-paper)] px-6 pb-4 pt-4">
        {isPreview ? (
          <div data-testid="preview-cta-banner"
            className="flex h-[54px] w-full items-center justify-center gap-2 rounded-[16px] bg-[#f2f0ec] text-[14px] font-semibold text-[#888888]">
            <Eye size={17} strokeWidth={2} /> Preview · this is what your clients see
          </div>
        ) : (
          <button data-testid="book-now" disabled={!canBook} onClick={proceed}
            className="flex h-[54px] w-full items-center justify-center gap-2 rounded-[16px] bg-[#111111] text-[15px] font-semibold text-white transition-all enabled:active:scale-[0.99] disabled:bg-[#e5e2dd] disabled:text-[#aaaaaa]">
            {!active ? "Not accepting bookings" : canBook ? <>Book this slot · {service ? displayPrice(service) : ""} <ArrowRight size={18} strokeWidth={2} /></> : "Select a service, date & time"}
          </button>
        )}
        {/* Powered by footer */}
        <div className="mt-2.5 flex justify-center">
          <a href="https://www.jothidakural.com" target="_blank" rel="noreferrer"
            className="flex flex-row items-center justify-center gap-1 text-[10px] text-[#888888]">
            Powered by <img src="/logo.svg" alt="JothidaKural" style={{ height: '18px', width: 'auto', display: 'block' }} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default PublicBooking;
